import { useEffect, useState, useCallback } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlarmClock, Plus, Undo2, Trash2, PlayCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

function AddFeeDialog({ leaseId, open, setOpen, onDone }) {
  const now = new Date();
  const [form, setForm] = useState({ period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`, amount: "", description: "Late fee" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.amount) return toast.error("Enter an amount");
    setSaving(true);
    try {
      await api.post("/charges", { lease_id: leaseId, period: form.period, amount: Number(form.amount), description: form.description });
      toast.success("Fee posted to the tenant's ledger");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="add-fee-dialog">
        <DialogHeader><DialogTitle>Add a fee</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Month</Label><Input type="month" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="50" data-testid="fee-amount" /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="fee-description" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="w-full" data-testid="fee-save">{saving ? "Posting…" : "Post fee"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChargeRow({ c, onWaive, onRemove }) {
  const muted = c.waived ? "text-slate-400 line-through" : "";
  const origin = c.created_by === "auto" ? "automatic" : "added by you";
  return (
    <div className="flex items-center justify-between py-2.5 gap-3" data-testid={`charge-row-${c.id}`}>
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${muted || "text-slate-900"}`}>{c.description}</div>
        <div className="text-xs text-slate-500">{c.period_label} · {c.fee_date} · {origin}{c.waived ? " · waived" : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`font-mono text-sm font-semibold ${muted || "text-rose-700"}`}>{fmtMoney(c.amount)}</span>
        <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={() => onWaive(c)} data-testid={`charge-waive-${c.id}`}>
          <Undo2 className="h-3.5 w-3.5" /> {c.waived ? "Reinstate" : "Waive"}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600" onClick={() => onRemove(c)} data-testid={`charge-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function ChargesPanel({ leaseId, onChange }) {
  const [charges, setCharges] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => api.get("/charges", { params: { lease_id: leaseId } }).then((r) => setCharges(r.data)), [leaseId]);
  useEffect(() => { if (leaseId) load(); }, [leaseId, load]);

  const refresh = () => { load(); onChange?.(); };
  const act = async (fn, okMsg) => {
    try { const res = await fn(); toast.success(typeof okMsg === "function" ? okMsg(res) : okMsg); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const waive = (c) => act(() => api.put(`/charges/${c.id}/waive`), (r) => (r.data.waived ? "Fee waived" : "Fee reinstated"));
  const remove = (c) => act(() => api.delete(`/charges/${c.id}`), "Fee removed");
  const runNow = async () => {
    setRunning(true);
    await act(() => api.post("/late-fees/run"), (r) => (r.data.count ? `Posted ${r.data.count} late fee(s)` : "No past-due copays — nothing posted"));
    setRunning(false);
  };

  if (!charges) return null;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 p-4" data-testid="charges-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h4 className="font-display font-bold text-slate-900 flex items-center gap-2"><AlarmClock className="h-4 w-4 text-amber-600" /> Late fees & charges</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={runNow} disabled={running} data-testid="charges-run-now">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Check late fees
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="charges-add"><Plus className="h-3.5 w-3.5" /> Add fee</Button>
        </div>
      </div>
      {charges.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4" data-testid="charges-empty">No fees on this lease. Automatic late fees post after the grace period set in Settings.</p>
      ) : (
        <div className="divide-y divide-slate-100">{charges.map((c) => <ChargeRow key={c.id} c={c} onWaive={waive} onRemove={remove} />)}</div>
      )}
      <AddFeeDialog leaseId={leaseId} open={addOpen} setOpen={setAddOpen} onDone={refresh} />
    </div>
  );
}
